import React, { lazy } from "react";
import { withLazyRoute } from "@/components/loading/LazyRouteWrapper";
import { Navigation } from "@/types/navigation";
import {
  Home as HomeIcon,
  Person2,
  AccessTime as AccessTimeIcon,
  BorderColor as BorderColorIcon,
  ManageSearch,
  AttachMoney as MoneyIcon,
  ViewKanban as ViewKanbanIcon,
  Savings as SavingsIcon,
  VideoLibrary as VideoLibraryIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { UserInfo } from "@freetech/models";

const TimeTracker = withLazyRoute(
  lazy(() => import("views/Shared/TimeTracker").then(m => ({ default: m.TimeTracker }))),
  "Loading time tracker..."
);
const FreeloHome = withLazyRoute(
  lazy(() => import("views/Shared/FreeloHome").then(m => ({ default: m.FreeloHome }))),
  "Loading freelancer home..."
);
const FreeloBoard = withLazyRoute(
  lazy(() => import("views/Shared/FreeloBoard")),
  "Loading freelancer board..."
);
const MyMeetingsView = withLazyRoute(
  lazy(() => import("views/Shared/MyMeetings/MyMeetingsView").then(m => ({ default: m.MyMeetingsView }))),
  "Loading meetings view..."
);
const SharedMeetingDetails = withLazyRoute(
  lazy(() =>
    import("views/Shared/MyMeetings/SharedMeetingDetails/SharedMeetingDetails").then(m => ({
      default: m.SharedMeetingDetails,
    }))
  ),
  "Loading meeting details..."
);

const FreelancerHome = withLazyRoute(
  lazy(() => import("@/views/Dashboards/Freelancer")),
  "Loading freelancer home..."
);
const FreelancerProfile = withLazyRoute(
  lazy(() => import("views/Freelancer/FreelancerProfile")),
  "Loading profile..."
);
const FreelancerSignAgreements = withLazyRoute(
  lazy(() => import("views/Freelancer/FreelancerSignAgreements")),
  "Loading agreements..."
);
const FreelancerProjectExplorer = withLazyRoute(
  lazy(() => import("views/Freelancer/FreelancerProjectExplorer")),
  "Loading project explorer..."
);
const MyClients = withLazyRoute(
  lazy(() => import("views/Freelancer/MyClients")),
  "Loading clients..."
);
const FreelancerClient = withLazyRoute(
  lazy(() => import("views/Freelancer/FreelancerClient")),
  "Loading client details..."
);
const ReferralProgram = withLazyRoute(
  lazy(() => import("views/Freelancer/ReferralProgram")),
  "Loading referral program..."
);
const FreelancerPayroll = withLazyRoute(
  lazy(() => import("views/Freelancer/FreelancerPayroll")),
  "Loading payroll..."
);

const BlogWriter = withLazyRoute(
  lazy(() => import("@/views/Admin/BlogWriter")),
  "Loading blog writer..."
);
const BlogPostEditor = withLazyRoute(
  lazy(() => import("@/views/Admin/BlogWriter/BlogPostEditor")),
  "Loading blog editor..."
);

export const getFreelancerPages = (showPayrollAndProfile: boolean = true, userInfo?: UserInfo): Navigation => {
  if (!userInfo) {
    return [];
  }
  return [
    { kind: "header", title: "General" },
    { kind: "divider" },
    {
      segment: "freelancer",
      title: "Home",
      icon: <HomeIcon />,
      component: <FreelancerHome />,
    },
    ...(showPayrollAndProfile
      ? [
          {
            segment: "freelancer/profile",
            title: "Profile",
            icon: <Person2 />,
            component: <FreelancerProfile />,
          },
          {
            segment: "freelancer/freelancer-payroll",
            title: "Payroll",
            icon: <MoneyIcon />,
            component: <FreelancerPayroll />,
          },
          {
            segment: "freelancer/timesheet",
            title: "Time Tracker",
            icon: <AccessTimeIcon />,
            component: <TimeTracker />,
          },
        ]
      : []),
    {
      segment: "freelancer/sign-agreements",
      title: "Contract Center",
      icon: <BorderColorIcon />,
      component: <FreelancerSignAgreements />,
    },
    { kind: "header", title: "Projects" },
    { kind: "divider" },
    {
      segment: "freelancer/my-clients",
      title: "My Clients",
      icon: <Person2 />,
      component: <MyClients />,
      slugBabyPattern: ":clientId",
      slugBabyComponent: <FreelancerClient />,
    },
    {
      kind: "page",
      title: "Freelo",
      segment: "freelo",
      icon: <ViewKanbanIcon />,
      component: <FreeloHome />,
      slugBabyPattern: ":boardId",
      slugBabyComponent: <FreeloBoard />,
    },
    {
      segment: "freelancer/my-meetings",
      title: "My Meetings",
      icon: <VideoLibraryIcon />,
      component: <MyMeetingsView />,
      slugBabyPattern: ":meetingId",
      slugBabyComponent: <SharedMeetingDetails />,
    },
    {
      segment: "freelancer/project-explorer",
      title: "Explorer",
      icon: <ManageSearch />,
      component: <FreelancerProjectExplorer />,
    },
    {
      segment: "freelancer/referral-program",
      title: "Referral Program",
      icon: <SavingsIcon />,
      component: <ReferralProgram />,
    },
    ...(userInfo.email === "jack@freetech.co" || userInfo.email === "adam1.siwiec@gmail.com"
      ? [
          {
            segment: "blog-writer",
            title: "Blog Writer",
            icon: <DescriptionIcon />,
            component: <BlogWriter />,
            slugBabyPattern: ":blogId",
            slugBabyComponent: <BlogPostEditor />,
          },
        ]
      : []),
  ];
};
